import { useState } from 'react';

export default function EnrollPage() {
  const [selectedPlan, setSelectedPlan] = useState<'founding' | 'flex'>('founding');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employmentStatus: '',
    graduationDate: '',
    availability: '',
    paymentPlan: 'founding'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('employmentStatus', formData.employmentStatus);
      formDataToSend.append('graduationDate', formData.graduationDate);
      formDataToSend.append('availability', formData.availability);
      formDataToSend.append('paymentPlan', selectedPlan === 'founding' ? 'Founding Member - $997' : 'Flex Plan - $375 × 3 months');

      const response = await fetch('https://readdy.ai/api/form/d5re1e6tc768qf4bqis1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setFormSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <a href="/" className="cursor-pointer">
              <img 
                src="https://public.readdy.ai/ai/img_res/2000139d-511f-45b0-ba23-8d9ee9829d39.png" 
                alt="Clinical Mentorship Logo" 
                className="h-10 sm:h-12 w-auto"
              />
            </a>
          </div>
        </div>
      </nav>

      {!formSubmitted ? (
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center gap-2 bg-[#8B9B8E]/10 border border-[#8B9B8E]/20 px-4 py-2 rounded-full mb-4 sm:mb-6">
                <i className="ri-vip-crown-fill text-[#8B9B8E] text-sm"></i>
                <span className="text-sm font-medium text-[#8B9B8E]">Founding Member Enrollment</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#2B2B2B] mb-4 sm:mb-6">
                Investment in Your Clinical Future
              </h1>
              <p className="text-lg sm:text-xl text-[#6B6B6B] max-w-3xl mx-auto leading-relaxed">
                Secure your spot in the Inaugural Cohort of the PMHNP Foundations Mentorship
              </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-10 sm:mb-12">
              {/* Founding Member Plan */}
              <div 
                onClick={() => setSelectedPlan('founding')}
                className={`bg-white rounded-2xl p-6 sm:p-8 border-2 transition-all cursor-pointer ${
                  selectedPlan === 'founding' 
                    ? 'border-[#8B9B8E] shadow-lg' 
                    : 'border-[#E5E5E5] hover:border-[#8B9B8E]/50'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2 bg-[#8B9B8E] text-white px-4 py-2 rounded-full">
                    <i className="ri-vip-crown-fill"></i>
                    <span className="font-semibold text-sm">Founding Member</span>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'founding' 
                      ? 'border-[#8B9B8E] bg-[#8B9B8E]' 
                      : 'border-[#E5E5E5]'
                  }`}>
                    {selectedPlan === 'founding' && (
                      <i className="ri-check-line text-white text-sm"></i>
                    )}
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl sm:text-5xl font-bold text-[#2B2B2B]">$997</span>
                    <span className="text-lg text-[#6B6B6B]">one-time</span>
                  </div>
                  <p className="text-sm text-[#6B6B6B]">Full 8-week program access</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Lifetime founding member status</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Priority access to future programs</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Direct influence on case selection</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Small group intimacy (max 12 clinicians)</p>
                  </div>
                </div>
              </div>

              {/* Flex Payment Plan */}
              <div 
                onClick={() => setSelectedPlan('flex')}
                className={`bg-white rounded-2xl p-6 sm:p-8 border-2 transition-all cursor-pointer ${
                  selectedPlan === 'flex' 
                    ? 'border-[#8B9B8E] shadow-lg' 
                    : 'border-[#E5E5E5] hover:border-[#8B9B8E]/50'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="inline-flex items-center gap-2 bg-[#6B6B6B] text-white px-4 py-2 rounded-full">
                    <i className="ri-bank-card-line"></i>
                    <span className="font-semibold text-sm">Flex Plan</span>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'flex' 
                      ? 'border-[#8B9B8E] bg-[#8B9B8E]' 
                      : 'border-[#E5E5E5]'
                  }`}>
                    {selectedPlan === 'flex' && (
                      <i className="ri-check-line text-white text-sm"></i>
                    )}
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-4xl sm:text-5xl font-bold text-[#2B2B2B]">$375</span>
                    <span className="text-lg text-[#6B6B6B]">× 3 months</span>
                  </div>
                  <p className="text-sm text-[#6B6B6B]">Total: $1,125 over 3 months</p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Same full 8-week program access</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Founding member benefits included</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Flexible payment schedule</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-[#2B2B2B] text-sm">Start now, pay over time</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enrollment Form */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-[#E5E5E5]">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#2B2B2B] mb-2">
                Complete Your Enrollment
              </h2>
              <p className="text-[#6B6B6B] mb-8">
                Fill out the form below to secure your spot in the inaugural cohort
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#2B2B2B] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E5E5E5] rounded-lg text-[#2B2B2B] focus:outline-none focus:border-[#8B9B8E] transition-colors text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#2B2B2B] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E5E5E5] rounded-lg text-[#2B2B2B] focus:outline-none focus:border-[#8B9B8E] transition-colors text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#2B2B2B] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E5E5E5] rounded-lg text-[#2B2B2B] focus:outline-none focus:border-[#8B9B8E] transition-colors text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Employment Status */}
                <div>
                  <label htmlFor="employmentStatus" className="block text-sm font-medium text-[#2B2B2B] mb-2">
                    Current Employment Status *
                  </label>
                  <select
                    id="employmentStatus"
                    name="employmentStatus"
                    required
                    value={formData.employmentStatus}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E5E5E5] rounded-lg text-[#2B2B2B] focus:outline-none focus:border-[#8B9B8E] transition-colors text-sm cursor-pointer"
                  >
                    <option value="">Select your status</option>
                    <option value="Currently Employed - Outpatient">Currently Employed - Outpatient</option>
                    <option value="Currently Employed - Inpatient">Currently Employed - Inpatient</option>
                    <option value="Currently Employed - Telehealth">Currently Employed - Telehealth</option>
                    <option value="Seeking Employment">Seeking Employment</option>
                    <option value="Recent Graduate">Recent Graduate</option>
                  </select>
                </div>

                {/* Graduation Date */}
                <div>
                  <label htmlFor="graduationDate" className="block text-sm font-medium text-[#2B2B2B] mb-2">
                    PMHNP Graduation Date *
                  </label>
                  <input
                    type="text"
                    id="graduationDate"
                    name="graduationDate"
                    required
                    value={formData.graduationDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E5E5E5] rounded-lg text-[#2B2B2B] focus:outline-none focus:border-[#8B9B8E] transition-colors text-sm"
                    placeholder="e.g., May 2024"
                  />
                </div>

                {/* Availability */}
                <div>
                  <label htmlFor="availability" className="block text-sm font-medium text-[#2B2B2B] mb-2">
                    Weekly Availability for Live Sessions *
                  </label>
                  <textarea
                    id="availability"
                    name="availability"
                    required
                    rows={3}
                    maxLength={500}
                    value={formData.availability}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#E5E5E5] rounded-lg text-[#2B2B2B] focus:outline-none focus:border-[#8B9B8E] transition-colors text-sm resize-none"
                    placeholder="e.g., Thursdays 6-8pm EST, Saturdays 10am-12pm EST"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-1">Maximum 500 characters</p>
                </div>

                {/* Selected Plan Display */}
                <div className="bg-[#8B9B8E]/10 border border-[#8B9B8E]/20 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#2B2B2B] mb-1">Selected Plan:</p>
                      <p className="text-lg font-semibold text-[#8B9B8E]">
                        {selectedPlan === 'founding' ? 'Founding Member - $997' : 'Flex Plan - $375 × 3 months'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="text-sm text-[#8B9B8E] hover:text-[#7a8a7d] font-medium cursor-pointer whitespace-nowrap"
                    >
                      Change Plan
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-[#8B9B8E] text-white rounded-full text-base font-semibold hover:bg-[#7a8a7d] transition-all cursor-pointer whitespace-nowrap active:scale-95 shadow-lg hover:shadow-xl"
                >
                  Complete Enrollment
                </button>

                <p className="text-xs text-[#6B6B6B] text-center leading-relaxed">
                  By submitting this form, you agree to be contacted regarding enrollment details and payment processing.
                </p>
              </form>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-8 sm:p-12 border border-[#E5E5E5] shadow-lg">
              <div className="w-20 h-20 bg-[#8B9B8E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-checkbox-circle-fill text-[#8B9B8E] text-5xl"></i>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-serif text-[#2B2B2B] mb-4">
                Welcome to the Founding Cohort!
              </h2>
              
              <p className="text-lg text-[#6B6B6B] mb-8 leading-relaxed">
                Thank you for enrolling in the PMHNP Foundations Mentorship. You'll receive a confirmation email shortly with next steps and payment details.
              </p>

              <div className="bg-[#FAFAF8] rounded-xl p-6 mb-8">
                <h3 className="text-xl font-semibold text-[#2B2B2B] mb-4">
                  Schedule Your 1-on-1 Welcome Chat
                </h3>
                <p className="text-[#6B6B6B] mb-6 leading-relaxed">
                  Let's connect for a brief welcome call to answer any questions and ensure you're set up for success.
                </p>
                <a
                  href="https://calendly.com/havenpsychiatrynp/free-15-minute-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#8B9B8E] text-white rounded-full text-base font-medium hover:bg-[#7a8a7d] transition-all cursor-pointer whitespace-nowrap active:scale-95"
                >
                  Book Your Welcome Call
                  <i className="ri-calendar-line"></i>
                </a>
              </div>

              <a
                href="/"
                className="inline-flex items-center gap-2 text-[#8B9B8E] hover:text-[#7a8a7d] font-medium cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Return to Homepage
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6 lg:px-12 bg-[#1a1a1a] text-center">
        <p className="text-[#B5B5B5] text-xs sm:text-sm">
          © 2025 Margaret Ngumi
        </p>
        <a 
          href="https://readdy.ai/?ref=logo" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-[#8B9B8E] text-xs sm:text-sm hover:text-[#7a8a7d] transition-colors mt-2 inline-block cursor-pointer"
        >
          Powered by Readdy
        </a>
      </footer>
    </div>
  );
}
